import { useCallback, useEffect, useRef, useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import { useDocumentLibraryPolling } from './useDocumentLibraryPolling';
import { errText } from './videoLibraryErrorText';

function useLibraryList(backendService: BackendService, fundOpportunityId: string) {
  const [documents, setDocuments] = useState<DocumentAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef(search);
  searchRef.current = search;

  const refresh = useCallback(async () => {
    try {
      setDocuments(
        await backendService.listDocumentLibrary({
          title: searchRef.current || undefined,
          fundOpportunityId,
        }),
      );
      setError(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  }, [backendService, fundOpportunityId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { documents, loading, error, search, setSearch, refresh };
}

/**
 * The document library: search, upload, edit metadata, and delete.
 * PDFs finish in complete(); Office files stay PENDING until the worker
 * converts them, so the list polls while any card is still processing.
 */
export function useDocumentLibrary(opts: {
  fundOpportunityId: string;
  backendService: BackendService;
}) {
  const { fundOpportunityId, backendService } = opts;
  const list = useLibraryList(backendService, fundOpportunityId);
  const isProcessing = useDocumentLibraryPolling(list.documents, list.refresh);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const upload = async (
    file: File,
    title: string,
    description: string,
  ): Promise<DocumentAsset | null> => {
    setUploading(true);
    setUploadError(null);
    try {
      const created = await backendService.uploadDocumentToLibrary(
        title,
        description,
        fundOpportunityId,
        file,
      );
      await list.refresh();
      return created;
    } catch (err) {
      setUploadError(errText(err));
      return null;
    } finally {
      setUploading(false);
    }
  };

  const updateMetadata = async (
    externalId: string,
    fields: { title?: string; description?: string },
  ) => {
    await backendService.updateDocumentMetadata(externalId, fields);
    await list.refresh();
  };

  const remove = async (externalId: string, force = false) => {
    await backendService.deleteDocumentAsset(externalId, force);
    await list.refresh();
  };

  return {
    ...list,
    uploading,
    isProcessing,
    uploadError,
    dismissUploadError: () => setUploadError(null),
    upload,
    updateMetadata,
    remove,
  };
}
